import { isAfter } from 'date-fns'
import { Commit, MergeCommit, isMergeCommit } from '../replicache'
import { invariant } from '@epic-web/invariant'

export type GetCommitFn = (id: string) => Promise<Commit | null>

export type CommitLineageResult = {
  ahead: number
  behind: number
  commonAncestor: Commit | undefined
  forkPoint: Commit | null
  diffCommitLineage: Commit[]
  merges: MergeCommit[]
}

export async function calculateCommitLineage({
  sourceCommitId,
  headCommitId,
  getCommit,
}: {
  sourceCommitId: string | null
  headCommitId: string | null
  getCommit: GetCommitFn
}): Promise<CommitLineageResult> {
  let ahead = -1
  let behind = -1

  const sourceCommitLineage: string[] = sourceCommitId ? [sourceCommitId] : []
  const headCommitLineage: string[] = headCommitId ? [headCommitId] : []
  const processedCommits = new Map<string, Commit>()

  let forkPointCommitId: string | undefined
  let forkPointIndexInSourceCommitLineage = -1
  const updates: MergeCommit[] = []
  const merges: MergeCommit[] = []
  const possibleCommonAncestors: Commit[] = []
  const deferred: (() => Promise<void>)[] = []

  async function processCommit(commit: Commit) {
    processedCommits.set(commit.id, commit)
    const indexInSourceCommitLineage = sourceCommitLineage.findIndex(
      (s) => s === commit.id
    )
    const indexInHeadCommitLineage = headCommitLineage.findIndex(
      (h) => h === commit.id
    )

    const isForkPoint =
      indexInSourceCommitLineage !== -1 && indexInHeadCommitLineage !== -1

    if (isForkPoint) {
      if (forkPointCommitId) {
        throw new Error(
          `encountered another fork point, which means we're processing commits beyond the initial fork point, which is innefficient.`
        )
      }
      forkPointCommitId = commit.id
      forkPointIndexInSourceCommitLineage = indexInSourceCommitLineage
      possibleCommonAncestors.push(commit)
    }

    if (isMergeCommit(commit)) {
      // defer cause it depends on full lineages
      deferred.push(async () => {
        const sourceMergeCommitIdx = headCommitLineage.findIndex(
          (h) => h === commit.mergeParentCommitId
        )
        const headMergeCommitIdx = sourceCommitLineage.findIndex(
          (h) => h === commit.mergeParentCommitId
        )

        if (sourceMergeCommitIdx !== -1) {
          updates.push(commit)
          const mergeParentCommit =
            processedCommits.get(commit.mergeParentCommitId) ??
            (await getCommit(commit.mergeParentCommitId))
          if (mergeParentCommit) {
            possibleCommonAncestors.push(mergeParentCommit)
          }
        }

        if (headMergeCommitIdx !== -1) {
          merges.push(commit)
          const mergeParentCommit =
            processedCommits.get(commit.mergeParentCommitId) ??
            (await getCommit(commit.mergeParentCommitId))
          if (mergeParentCommit) {
            possibleCommonAncestors.push(mergeParentCommit)
          }
        }
      })
    }

    return { commit }
  }

  let shouldBreak = false
  while (!shouldBreak) {
    const currentSourceCommitId =
      sourceCommitLineage[sourceCommitLineage.length - 1]
    const currentHeadCommitId = headCommitLineage[headCommitLineage.length - 1]

    shouldBreak = true

    if (currentSourceCommitId) {
      const commit = await getCommit(currentSourceCommitId)
      invariant(commit, `commit not found: ${currentSourceCommitId}`)
      if (commit.parentCommitId) {
        shouldBreak = false
        sourceCommitLineage.push(commit.parentCommitId)
      }
      await processCommit(commit)
    }

    if (forkPointCommitId) break

    if (currentHeadCommitId) {
      const commit = await getCommit(currentHeadCommitId)
      invariant(commit, `commit not found: ${currentHeadCommitId}`)
      if (commit.parentCommitId) {
        shouldBreak = false
        headCommitLineage.push(commit.parentCommitId)
      }
      await processCommit(commit)
    }

    if (forkPointCommitId) break
  }

  await Promise.all(deferred.map((cb) => cb()))

  const diffCommitLineage = sourceCommitLineage
    .splice(0, forkPointIndexInSourceCommitLineage)
    .map((cId) => processedCommits.get(cId)!)

  const commonAncestor = possibleCommonAncestors.length
    ? possibleCommonAncestors.reduce((newest, current) => {
        return isAfter(new Date(current.createdAt), new Date(newest.createdAt))
          ? current
          : newest
      })
    : undefined

  if (!commonAncestor) {
    // branches are unrelated
    behind = headCommitLineage.length
    ahead = sourceCommitLineage.length
  } else {
    behind = headCommitLineage.findIndex((cId) => {
      const commit = processedCommits.get(cId)
      if (!commit) return false
      if (commit.mergeParentCommitId === commonAncestor?.id) return true
      if (commit.id === commonAncestor?.id) return true
      if (commit.id === commonAncestor?.mergeParentCommitId) return true
      return false
    })
    /**
     * Ahead: the diffCommitLineage minus the index of the last merge.
     */
    ahead =
      diffCommitLineage.length -
      merges.reduce((acc, commit) => {
        if (!commit) return acc
        const commitsBeforeMerge = isMergeCommit(commit)
          ? [...diffCommitLineage]
              .reverse()
              .findIndex((s) => s.id === commit.mergeParentCommitId) + 1
          : 0
        if (commitsBeforeMerge > acc) return commitsBeforeMerge
        return acc
      }, 0)
  }

  return {
    ahead,
    behind,
    commonAncestor,
    forkPoint: forkPointCommitId ? await getCommit(forkPointCommitId) : null,
    diffCommitLineage,
    merges,
  }
}
